import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiArrowUpRight,
  FiArrowRight,
  FiSearch,
  FiBookOpen,
  FiX,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import API_URL from "../config/config";
import { useSEO } from "../utils/seo";
import { readBlogBootstrap } from "../utils/blogBootstrap";
import "./Blogs.css";

export const blogPath = (blog) => `/${blog.slug}/${blog.blogId}`;
export const blogDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
export async function fetchBlogApi(path, signal) {
  const response = await fetch(`${API_URL}/blogs${path}`, { signal });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(
      data.message || "Unable to load stories. Please try again.",
    );
    error.status = response.status;
    throw error;
  }
  return data;
}
export function BlogCard({ blog, featured = false }) {
  return (
    <Link
      to={blogPath(blog)}
      className={`journal-card ${featured ? "journal-card-featured" : ""}`}
    >
      <div className="journal-card-image">
        {blog.coverImage ? (
          <img
            src={blog.coverImage}
            alt={blog.coverAlt || ""}
            loading={featured ? "eager" : "lazy"}
          />
        ) : (
          <div className="journal-art">
            <FiBookOpen />
            <span>Ideas in motion.</span>
          </div>
        )}
        <span className="journal-image-arrow">
          <FiArrowUpRight />
        </span>
      </div>
      <div className="journal-card-copy">
        <div className="journal-card-category">
          <span>{blog.category}</span>
          <span>{blog.readingMinutes} min read</span>
        </div>
        <h2>{blog.title}</h2>
        <p>{blog.excerpt}</p>
        <div className="journal-card-byline">
          <span>{blog.author}</span>
          <time dateTime={blog.publishedAt}>{blogDate(blog.publishedAt)}</time>
        </div>
      </div>
    </Link>
  );
}
export default function Blogs() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "",
    category = params.get("category") || "",
    sort = params.get("sort") || "newest",
    page = Math.max(1, Number(params.get("page")) || 1);
  const [initialListing] = useState(() => readBlogBootstrap("listing"));
  const [input, setInput] = useState(q),
    [data, setData] = useState(initialListing || {
      items: [],
      categories: [],
      total: 0,
      pages: 0,
    }),
    [loading, setLoading] = useState(!initialListing),
    [error, setError] = useState(null),
    [retry, setRetry] = useState(0);
  useSEO({
    title: "The Instaport Journal — Stories, Ideas & Delivery Insights",
    description:
      "Fresh perspectives, practical guides and stories from the world of local delivery. Explore the Instaport journal.",
    path: page > 1 ? `/blogs?page=${page}` : "/blogs",
    noIndex: Boolean(q || category || sort !== "newest"),
    structuredData: [
      {
        "@type": "CollectionPage",
        name: "The Instaport Journal",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: data.items.map((b, i) => ({
            "@type": "ListItem",
            position: (page - 1) * 10 + i + 1,
            url: `https://instaportdelivery.com${blogPath(b)}`,
            name: b.title,
          })),
        },
      },
    ],
  });
  useEffect(() => setInput(q), [q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (input !== q)
        setParams(
          (p) => {
            const next = new URLSearchParams(p);
            input.trim() ? next.set("q", input.trim()) : next.delete("q");
            next.delete("page");
            return next;
          },
          { replace: true },
        );
    }, 350);
    return () => clearTimeout(timer);
  }, [input, q, setParams]);
  useEffect(() => {
    const controller = new AbortController();
    const initial = retry === 0 ? readBlogBootstrap("listing") : null;
    if (initial) {
      setData(initial);
      setLoading(false);
      setError(null);
      return () => controller.abort();
    }
    setLoading(true);
    setError(null);
    fetchBlogApi(
      `?${new URLSearchParams({ q, category, sort, page, limit: 10 })}`,
      controller.signal,
    )
      .then(setData)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q, category, sort, page, retry]);
  const filter = (key, value) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      value ? next.set(key, value) : next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  const featured =
    !q && !category && sort === "newest" && page === 1 && data.items.length > 0;
  return (
    <div className="journal-page">
      <Navbar />
      <main className="journal-main">
        <header className="journal-hero">
          <div className="journal-kicker">
            <span /> THE INSTAPORT JOURNAL
          </div>
          <div className="journal-hero-row">
            <h1>
              Good stories.
              <br />
              <em>Always moving.</em>
            </h1>
            <div className="journal-hero-aside">
              <p>
                Fresh perspectives, helpful guides and the people behind every
                delivery.
              </p>
              <a href="#journal-stories">
                Find your next read <FiArrowRight />
              </a>
            </div>
          </div>
          <div className="journal-hero-line">
            <span>IDEAS THAT GO THE EXTRA MILE</span>
            <FiBookOpen />
          </div>
        </header>
        <section id="journal-stories" className="journal-stories">
          <div className="journal-toolbar">
            <form
              className="journal-search"
              onSubmit={(e) => {
                e.preventDefault();
                filter("q", input.trim());
              }}
            >
              <FiSearch />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Search stories, topics or blog ID…"
                aria-label="Search stories, topics or blog ID"
              />
              {input && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setInput("");
                    filter("q", "");
                  }}
                >
                  <FiX />
                </button>
              )}
              <button type="submit">Search</button>
            </form>
            <label className="journal-sort">
              Sort by{" "}
              <select
                value={sort}
                onChange={(e) => filter("sort", e.target.value)}
              >
                <option value="newest">Latest stories</option>
                <option value="oldest">Oldest first</option>
                <option value="title">Title A–Z</option>
              </select>
            </label>
          </div>
          <div className="journal-categories" aria-label="Filter by category">
            <button
              aria-pressed={!category}
              onClick={() => filter("category", "")}
            >
              All stories
            </button>
            {[
              ...new Set([...data.categories, ...(category ? [category] : [])]),
            ].map((item) => (
              <button
                key={item}
                aria-pressed={category === item}
                onClick={() => filter("category", item)}
              >
                {item}
              </button>
            ))}
          </div>
          {loading ? (
            <div
              className="journal-skeletons"
              role="status"
              aria-label="Loading stories"
            >
              {[1, 2, 3].map((n) => (
                <div className="journal-skeleton" key={n}>
                  <div />
                  <span />
                  <span />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="journal-empty" role="alert">
              <FiBookOpen />
              <h2>
                {error.status === 404
                  ? "A new chapter is on its way"
                  : "We couldn’t load the journal"}
              </h2>
              <p>{error.message}</p>
              {error.status !== 404 && (
                <button onClick={() => setRetry((v) => v + 1)}>
                  Try again
                </button>
              )}
              <Link to="/">
                Back to home <FiArrowRight />
              </Link>
            </div>
          ) : !data.items.length ? (
            <div className="journal-empty">
              <FiSearch />
              <h2>
                {q || category
                  ? "No stories found. Yet."
                  : "Great stories are on their way."}
              </h2>
              <p>
                {q || category
                  ? "Try another keyword, blog ID or category."
                  : "Come back soon for fresh perspectives from the Instaport team."}
              </p>
              {(q || category) && (
                <button
                  onClick={() => {
                    setInput("");
                    setParams({});
                  }}
                >
                  Explore all stories
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="journal-section-label">
                <h2>
                  {q ? `Results for “${q}”` : category || "The latest dispatch"}
                </h2>
                <span>
                  {data.total} {data.total === 1 ? "story" : "stories"}
                </span>
              </div>
              {featured && <BlogCard blog={data.items[0]} featured />}
              <div className="journal-grid">
                {data.items.slice(featured ? 1 : 0).map((blog) => (
                  <BlogCard key={blog.blogId} blog={blog} />
                ))}
              </div>
              {data.pages > 1 && (
                <nav className="journal-pagination" aria-label="Journal pages">
                  <button
                    disabled={page <= 1}
                    onClick={() => filter("page", String(page - 1))}
                  >
                    ← Previous
                  </button>
                  <span>
                    {page} / {data.pages}
                  </span>
                  <button
                    disabled={page >= data.pages}
                    onClick={() => filter("page", String(page + 1))}
                  >
                    Next →
                  </button>
                </nav>
              )}
            </>
          )}
        </section>
        <aside className="journal-bottom-note">
          <span>A LITTLE CURIOSITY GOES A LONG WAY.</span>
          <h2>
            More than deliveries.
            <br />
            <em>Connections that matter.</em>
          </h2>
          <Link to="/about-us">
            Meet Instaport <FiArrowUpRight />
          </Link>
        </aside>
      </main>
      <Footer />
    </div>
  );
}
